import { useQuery } from '@tanstack/react-query';
import { Terminal, Activity, Database, RefreshCw } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { EmptyState } from '@/components/shared/EmptyState';
import { adminApi } from '@/services/api';
import { cn } from '@nepal-ecom/ui';
import { Badge, Button, Card, CardContent, Skeleton } from '@nepal-ecom/ui';

interface SystemHealth {
  status: string;
  service: string;
  version: string;
  environment: string;
  uptimeSeconds: number;
  startedAt: string;
  time: string;
}

function formatUptime(seconds: number): string {
  if (seconds < 60) return `${Math.floor(seconds)}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

export function SystemLogs() {
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['admin-system-health'],
    queryFn: () => adminApi.getSystemHealth(),
    select: (res) => res.data.data as SystemHealth,
    refetchInterval: 30_000,
  });

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="System"
        description="API health, environment, and (eventually) audit log."
        badge={<Badge variant="warning">Super-admin only</Badge>}
        actions={
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} /> Refresh
          </Button>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <Card><CardContent className="p-4">
          <div className="h-9 w-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center mb-2.5">
            <Activity className="h-4 w-4" />
          </div>
          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Status</p>
          {isLoading
            ? <Skeleton className="h-7 w-16 mt-1" />
            : <p className="text-2xl font-bold text-emerald-700 tabular-nums mt-0.5 capitalize">{data?.status ?? 'unknown'}</p>}
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="h-9 w-9 rounded-lg bg-cobalt-50 text-cobalt-600 flex items-center justify-center mb-2.5">
            <Terminal className="h-4 w-4" />
          </div>
          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Uptime</p>
          {isLoading
            ? <Skeleton className="h-7 w-16 mt-1" />
            : <p className="text-2xl font-bold text-slate-900 tabular-nums mt-0.5">{formatUptime(data?.uptimeSeconds ?? 0)}</p>}
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="h-9 w-9 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center mb-2.5">
            <Database className="h-4 w-4" />
          </div>
          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Environment</p>
          {isLoading
            ? <Skeleton className="h-7 w-16 mt-1" />
            : <p className="text-lg font-bold text-slate-900 mt-1.5 capitalize">{data?.environment ?? '—'}</p>}
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="h-9 w-9 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center mb-2.5">
            <Activity className="h-4 w-4" />
          </div>
          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Version</p>
          {isLoading
            ? <Skeleton className="h-7 w-16 mt-1" />
            : <p className="text-lg font-bold text-slate-900 tabular-nums mt-1.5">{data?.version ?? '—'}</p>}
        </CardContent></Card>
      </div>

      <Card>
        <CardContent className="p-0">
          <EmptyState
            icon={Terminal}
            title="Audit log coming soon"
            description="Detailed audit logging (vendor approvals, refunds, admin actions, security events) is not yet implemented in the API. Use server logs in the meantime."
          />
        </CardContent>
      </Card>
    </div>
  );
}
